import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Typography,
  makeStyles,
  Theme,
} from '@material-ui/core';
import { Content, Header, Page } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { type ProjectsApiOptions } from '../../api/projects';
import { canManageOrganization, useCurrentUser } from '../currentUser';
import { OrgAdminFilesPanel } from './OrgAdminFilesPanel';

const useStyles = makeStyles((theme: Theme) => ({
  message: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  subtle: {
    color: theme.palette.text.secondary,
  },
}));

export const OrgAdminPage = () => {
  const { t } = useTranslation('projects');
  const classes = useStyles();
  const config = useApi(configApiRef);
  const current = useCurrentUser();

  const backendUrl = config.getString('backend.baseUrl');
  const projectsProxyPath =
    config.getOptionalString('ses.projectsApi.proxyPath') ??
    '/api/proxy/ses-projects';

  const apiOptions = useMemo<ProjectsApiOptions>(
    () => ({ baseUrl: `${backendUrl}${projectsProxyPath}` }),
    [backendUrl, projectsProxyPath],
  );

  const message = (
    title: string | undefined,
    description: string,
    action?: ReactNode,
  ) => (
    <Paper variant="outlined" className={classes.message}>
      {title && <Typography variant="h6">{title}</Typography>}
      <Typography variant="body2" className={classes.subtle}>
        {description}
      </Typography>
      {action && <Box mt={2}>{action}</Box>}
    </Paper>
  );

  let content: ReactNode;
  if (current.status === 'ready') {
    const org = current.currentOrganization;
    content = canManageOrganization(org) ? (
      <OrgAdminFilesPanel
        orgId={org.id}
        apiOptions={apiOptions}
        isPreviewMode={false}
      />
    ) : (
      message(
        t('orgAdmin.page.noAccessTitle'),
        org.isPlatformStaff
          ? t('orgAdmin.page.noAccessStaffDescription')
          : t('orgAdmin.page.noAccessDescription'),
      )
    );
  } else if (current.status === 'preview') {
    // Sample data only; the panel ignores the id in preview mode.
    content = (
      <OrgAdminFilesPanel orgId="" apiOptions={apiOptions} isPreviewMode />
    );
  } else if (current.status === 'loading') {
    content = message(undefined, t('orgAdmin.page.loading'));
  } else {
    content = message(
      undefined,
      t('orgAdmin.page.loadError'),
      <Button color="primary" variant="outlined" onClick={current.reload}>
        {t('orgAdmin.page.retry')}
      </Button>,
    );
  }

  return (
    <Page themeId="home">
      <Header
        title={t('orgAdmin.page.title')}
        subtitle={t('orgAdmin.page.subtitle')}
      />
      <Content>{content}</Content>
    </Page>
  );
};

export const orgAdminPage = <OrgAdminPage />;
